// =============================================================================
// PEDRA — Otimizador de plano de corte 2D
// src/core/types.ts — CONTRATO DE TIPOS DE DOMINIO (fonte unica da verdade)
// =============================================================================
//
// UNIDADE INTERNA — LEIA ANTES DE MEXER EM QUALQUER COISA
// -----------------------------------------------------------------------------
// TODA dimensao, coordenada, posicao e comprimento neste arquivo (e em todo o
// nucleo do app) e um NUMERO INTEIRO em MILESIMOS DE MILIMETRO, ou seja, em
// MICROMETROS (µm).
//
//     1 mm  = 1_000 µm
//     1 cm  = 10_000 µm
//     1 m   = 1_000_000 µm
//     1 pol = 25_400 µm          (exato)
//     1 pe  = 304_800 µm         (exato)
//
// Motivo: aritmetica inteira elimina qualquer erro de ponto flutuante em kerf
// (espessura de serra), somas de cortes e testes de igualdade. `x + w + kerf`
// nunca produz 1199.9999999996.
//
// Fracoes imperiais ate 1/64" sao representadas com erro maximo de 0,125 µm
// (1/64" = 396,875 µm -> 397 µm). Fisicamente irrelevante (0,000125 mm), e o
// arredondamento e feito UMA vez, na entrada, nunca acumula.
//
// Faixa segura: inteiros exatos ate 2^53-1. Uma chapa de 6 m = 6_000_000 µm.
// Area de chapa 2750x1830 mm = 5,03e12 µm². Somar ~1.800 chapas ainda e exato.
// Acima disso a soma de areas perde o ultimo bit — irrelevante para percentuais.
//
// CONVENCAO DE NOMES: nada de branded type (o otimizador faz aritmetica intensa
// e `as Micron` em cada expressao seria insuportavel). A seguranca vem da
// disciplina: campos e variaveis tipados como `Micron` NUNCA recebem valor em
// mm sem passar por `toMicron()` de `./units`.
//
// SISTEMA DE COORDENADAS
// -----------------------------------------------------------------------------
// Origem no canto SUPERIOR-ESQUERDO da chapa (igual ao SVG, sem inversao de Y).
//   eixo X -> para a DIREITA, mede a LARGURA  (width)
//   eixo Y -> para BAIXO,     mede o COMPRIMENTO (length)
// Logo: `sheet.width` e a extensao em X e `sheet.length` a extensao em Y.
// Um corte de eixo 'x' e uma linha VERTICAL em x = pos (separa esquerda/direita).
// Um corte de eixo 'y' e uma linha HORIZONTAL em y = pos (separa cima/baixo).
// =============================================================================

/** Inteiro em milesimos de milimetro (micrometros). Ver cabecalho do arquivo. */
export type Micron = number;

/**
 * Area em micrometros quadrados (µm²). Ponto flutuante — nao e garantido inteiro
 * exato em somas gigantes, mas e exato para qualquer projeto real.
 * 1 mm² = 1e6 µm² · 1 m² = 1e12 µm².
 */
export type Micron2 = number;

/** Identificador de entidade. Gerado por `newId()` de `./defaults`. */
export type Id = string;

// -----------------------------------------------------------------------------
// UNIDADES E EXIBICAO
// -----------------------------------------------------------------------------

/** Unidade de ENTRADA e EXIBICAO. O armazenamento e sempre em Micron. */
export type Unit = 'mm' | 'cm' | 'm' | 'in' | 'ft';

/** Ordem em que as duas dimensoes aparecem na interface e nas tabelas. */
export type DimensionOrder = 'lengthFirst' | 'widthFirst';

/** Objetivo que o otimizador tenta minimizar primeiro. */
export type OptimizationPriority = 'leastWaste' | 'leastCuts' | 'smallerSheets';

/** Preferencia de direcao do PRIMEIRO corte de cada chapa. */
export type CutOrientation = 'optimal' | 'preferWidth' | 'preferLength';

/**
 * Direcao do veio / movimento da pedra. Codificacao numerica compativel com o
 * CutList Optimizer original:
 *   0 = sem veio        -> a peca pode girar 90 graus livremente
 *   1 = veio ao longo da LARGURA
 *   2 = veio ao longo do COMPRIMENTO
 */
export type GrainOrientation = 0 | 1 | 2;

/** Lado de uma peca retangular (usado para fita de borda e desenho). */
export type EdgeSide = 'top' | 'right' | 'bottom' | 'left';

/** Politica de agrupamento de chapas com plano de corte identico. */
export type StackMode = 'auto' | 'never' | 'always';

/** Eixo de um corte. 'x' = linha vertical; 'y' = linha horizontal. */
export type CutAxis = 'x' | 'y';

// -----------------------------------------------------------------------------
// GEOMETRIA BASICA
// -----------------------------------------------------------------------------

/** Retangulo em coordenadas absolutas da chapa. Tudo em Micron. */
export interface Rect {
  x: Micron;
  y: Micron;
  /** Extensao no eixo X. */
  width: Micron;
  /** Extensao no eixo Y. */
  length: Micron;
}

// -----------------------------------------------------------------------------
// FITA DE BORDA
// -----------------------------------------------------------------------------

/** Um tipo de fita cadastrado no projeto (ex.: "PVC 22x0,45 Branco"). */
export interface EdgeBandType {
  id: Id;
  /** Nome exibido nos seletores e no relatorio de consumo. */
  name: string;
  /** Espessura da fita. */
  thickness: Micron;
  /** Cor hex #RRGGBB usada para desenhar a faixa na aresta da peca. */
  color: string;
}

/**
 * Fita aplicada em cada lado da peca. Cada valor e o `EdgeBandType.id`
 * ou `null` quando aquele lado nao leva fita.
 * Os lados sao definidos na orientacao ORIGINAL da peca (antes de girar).
 */
export interface EdgeBanding {
  top: Id | null;
  right: Id | null;
  bottom: Id | null;
  left: Id | null;
}

// -----------------------------------------------------------------------------
// PECA (PAINEL A PRODUZIR)
// -----------------------------------------------------------------------------

export interface Part {
  id: Id;
  /** Comprimento — extensao no eixo Y quando a peca NAO esta girada. */
  length: Micron;
  /** Largura — extensao no eixo X quando a peca NAO esta girada. */
  width: Micron;
  /** Quantidade de copias identicas. Inteiro >= 0 (0 mantem a linha sem usar). */
  qty: number;
  /** Rotulo livre desenhado dentro da peca no diagrama. Pode repetir. */
  label: string;
  /** Nome do material (texto livre, casado por igualdade exata). '' = sem material. */
  material: string;
  /** Direcao do veio desta peca. */
  orientation: GrainOrientation;
  /** false = linha ignorada no calculo, mas mantida na lista. */
  enabled: boolean;
  /** Fita de borda por lado. */
  edgeBanding: EdgeBanding;
  /** Cor de preenchimento no diagrama (#RRGGBB). Vem de PART_COLORS. */
  color: string;
}

// -----------------------------------------------------------------------------
// CHAPA DE ESTOQUE
// -----------------------------------------------------------------------------

export interface StockSheet {
  id: Id;
  /** Comprimento da chapa bruta — extensao no eixo Y. */
  length: Micron;
  /** Largura da chapa bruta — extensao no eixo X. */
  width: Micron;
  /** Quantidade disponivel. `null` = estoque ILIMITADO desta chapa. */
  qty: number | null;
  /** Rotulo da chapa, exibido no cabecalho de cada diagrama. */
  label: string;
  /** Nome do material. Casado com `Part.material` quando `considerMaterial`. */
  material: string;
  /** Direcao do veio DA CHAPA. */
  orientation: GrainOrientation;
  enabled: boolean;
}

// -----------------------------------------------------------------------------
// CONFIGURACOES
// -----------------------------------------------------------------------------

export interface Settings {
  /** Espessura da serra/disco/fio (kerf). Perdida a cada corte. */
  kerf: Micron;
  /** Respeitar a direcao do veio (impede rotacao de 90 graus). */
  considerGrain: boolean;
  /** Segregar pecas e chapas por material. */
  considerMaterial: boolean;
  /** Habilita a coluna de fita de borda e os totais de fita. */
  edgeBandingEnabled: boolean;
  /** Mostra a coluna "Nome" e desenha os rotulos dentro das pecas. */
  labelsOnPanels: boolean;
  /** Usar apenas UMA unidade de cada chapa do estoque. */
  useSingleStockUnit: boolean;
  /** Unidade de entrada/exibicao. Nao altera os dados armazenados. */
  unit: Unit;
  /** Ordem de exibicao das dimensoes. */
  dimensionOrder: DimensionOrder;
  /** Casas decimais na exibicao (0 a 3). */
  decimalPlaces: number;
  /** Menor tira de aparo/sobra que o algoritmo pode produzir. */
  minTrimDimension: Micron;
  /** Agrupar chapas com plano de corte identico num unico diagrama. */
  stackSamePanels: StackMode;
  /** Objetivo principal da otimizacao. */
  priority: OptimizationPriority;
  /** Preferencia de direcao do primeiro corte. */
  cutOrientation: CutOrientation;
  /** Teto de tempo do solver, em milissegundos. */
  timeBudgetMs: number;
  /** Semente do PRNG. Garante resultado REPRODUTIVEL. */
  seed: number;
}

// -----------------------------------------------------------------------------
// RESULTADO — POSICIONAMENTO
// -----------------------------------------------------------------------------

export interface Placement {
  /** Peca de origem (`Part.id`). */
  partId: Id;
  /** Indice da copia: 0..qty-1. `${partId}#${instance}` e unico no projeto. */
  instance: number;
  /** Copia de `Part.label` no momento da otimizacao (o resultado e um snapshot). */
  label: string;
  /** Canto SUPERIOR-ESQUERDO, em coordenadas absolutas da chapa. */
  x: Micron;
  y: Micron;
  /** Extensao no eixo Y, JA aplicada a rotacao. */
  length: Micron;
  /** Extensao no eixo X, JA aplicada a rotacao. */
  width: Micron;
  /** true = a peca foi girada 90 graus (length/width trocados em relacao a Part). */
  rotated: boolean;
  /** Cor de preenchimento (copiada de `Part.color`). */
  color: string;
  /** Material da peca (copiado de `Part.material`). */
  material: string;
  /**
   * Fita de borda JA rotacionada para a orientacao final do desenho.
   * Quando `rotated`, os lados giram: top<-left, right<-top, bottom<-right, left<-bottom.
   */
  edgeBanding: EdgeBanding;
}

// -----------------------------------------------------------------------------
// RESULTADO — SOBRAS
// -----------------------------------------------------------------------------

/**
 * Area livre remanescente na chapa. `FreeArea` e o nome usado no otimizador;
 * `Offcut` e o alias usado na interface. Sao o MESMO tipo.
 */
export interface FreeArea {
  id: Id;
  x: Micron;
  y: Micron;
  width: Micron;
  length: Micron;
  area: Micron2;
  /**
   * true quando os dois lados sao >= `Settings.minTrimDimension`, ou seja,
   * a sobra e reaproveitavel. false = desperdicio puro.
   */
  usable: boolean;
}

/** Alias de `FreeArea`. Use o nome que ficar mais legivel no contexto. */
export type Offcut = FreeArea;

// -----------------------------------------------------------------------------
// RESULTADO — SEQUENCIA DE CORTES
// -----------------------------------------------------------------------------

/** Um dos dois retangulos gerados por um corte. Alimenta a coluna "Resultado". */
export interface CutChild {
  x: Micron;
  y: Micron;
  width: Micron;
  length: Micron;
  /** true = este filho e sobra/refugo, nao contem peca util. */
  isWaste: boolean;
}

/** Um corte fisico, na ordem em que o operador deve executa-lo. */
export interface CutInstruction {
  /** Ordem de execucao dentro da chapa, 1-based. */
  seq: number;
  /** Indice da chapa (`SheetLayout.index`) a que este corte pertence. */
  sheetIndex: number;
  /** 'x' = corte VERTICAL em x = pos · 'y' = corte HORIZONTAL em y = pos. */
  axis: CutAxis;
  /** Coordenada onde a lamina COMECA a remover material. */
  pos: Micron;
  /** Inicio da extensao do corte no eixo perpendicular. */
  start: Micron;
  /** Fim da extensao do corte no eixo perpendicular. */
  end: Micron;
  /** = end - start. Redundante, evita recalculo em relatorio. */
  length: Micron;
  /** Material destruido por esta passada (normalmente `Settings.kerf`). */
  kerf: Micron;
  /**
   * Estagio de guilhotina: 1 = corte passante na chapa inteira,
   * 2 = transversal ao anterior, 3 = paralelo ao de estagio 1 dentro da faixa...
   * Cortes PARALELOS ao anterior ficam no MESMO estagio.
   */
  stage: number;
  /** Retangulo que estava sendo dividido (para desenhar e para a tabela). */
  parent: Rect;
  /** Retangulo resultante do lado menor de coordenada (esquerda / cima). */
  childA: CutChild;
  /** Retangulo resultante do lado maior de coordenada (direita / baixo). */
  childB: CutChild;
}

// -----------------------------------------------------------------------------
// RESULTADO — CHAPA
// -----------------------------------------------------------------------------

export interface SheetLayout {
  /** Indice global sequencial, 0-based. Usado em `CutInstruction.sheetIndex`. */
  index: number;
  /** Chapa de estoque usada (`StockSheet.id`). */
  stockSheetId: Id;
  /** Copia de `StockSheet.label` no momento da otimizacao. */
  label: string;
  /** Copia de `StockSheet.material`. */
  material: string;
  /** Largura da chapa (eixo X). */
  width: Micron;
  /** Comprimento da chapa (eixo Y). */
  length: Micron;
  /**
   * Quantas chapas FISICAS usam este mesmo plano de corte.
   * >1 apenas quando `Settings.stackSamePanels` agrupa layouts identicos.
   */
  repeat: number;
  placements: Placement[];
  /** Areas livres (sobras + desperdicio). Ver `FreeArea.usable`. */
  offcuts: FreeArea[];
  cuts: CutInstruction[];
  /** Soma da area das pecas colocadas. */
  usedArea: Micron2;
  /** = area total da chapa - usedArea. */
  wasteArea: Micron2;
  /** usedArea / (width * length), de 0 a 1. */
  usedAreaRatio: number;
  /** Soma de `CutInstruction.length` desta chapa. */
  cutLength: Micron;
  /** = cuts.length. */
  numCuts: number;
}

// -----------------------------------------------------------------------------
// RESULTADO — PECAS QUE NAO COUBERAM
// -----------------------------------------------------------------------------

export type UnfitReason =
  /** Maior que qualquer chapa habilitada, mesmo girada. */
  | 'tooLarge'
  /** Caberia girada, mas a direcao do veio nao permite. */
  | 'grainConflict'
  /** Nao ha chapa habilitada com o material desta peca. */
  | 'noMaterialStock'
  /** Acabou a quantidade de chapas disponiveis. */
  | 'stockExhausted'
  /** Dimensao/quantidade invalida detectada na preparacao. */
  | 'invalid';

export interface UnfitPart {
  partId: Id;
  label: string;
  length: Micron;
  width: Micron;
  material: string;
  /** Quantas copias NAO couberam. */
  qty: number;
  reason: UnfitReason;
  /** Mensagem pt-BR pronta para exibir, ja explicando o motivo. */
  message: string;
}

// -----------------------------------------------------------------------------
// ENTRADA E SAIDA DA OTIMIZACAO
// -----------------------------------------------------------------------------

export interface OptimizationInput {
  parts: Part[];
  stock: StockSheet[];
  settings: Settings;
  /** Cadastro de fitas, necessario para calcular os totais por tipo. */
  edgeBands: EdgeBandType[];
}

export interface OptimizationStats {
  /** Chapas FISICAS usadas (soma de `SheetLayout.repeat`). */
  numSheets: number;
  /** Area bruta de todas as chapas usadas. */
  totalSheetArea: Micron2;
  /** Area ocupada por pecas uteis. */
  totalUsedArea: Micron2;
  /** = totalSheetArea - totalUsedArea. */
  totalWasteArea: Micron2;
  /** totalUsedArea / totalSheetArea, de 0 a 1. */
  usedAreaRatio: number;
  /** totalWasteArea / totalSheetArea, de 0 a 1. */
  wasteRatio: number;
  /** Soma do comprimento de todos os cortes. */
  totalCutLength: Micron;
  /** Numero total de cortes. */
  numCuts: number;
  /** Copias de pecas efetivamente posicionadas. */
  numPlacedParts: number;
  /** Copias de pecas que nao couberam (soma de `UnfitPart.qty`). */
  numUnfitParts: number;
  /** Metros lineares de fita por tipo: `{ [EdgeBandType.id]: Micron }`. */
  edgeBandingTotals: Record<Id, Micron>;
  /** Tempo de calculo em milissegundos. */
  elapsedMs: number;
}

export interface OptimizationResult {
  sheets: SheetLayout[];
  stats: OptimizationStats;
  unfit: UnfitPart[];
  /** Avisos em pt-BR gerados durante o calculo. Sempre presente (pode ser []). */
  warnings: string[];
}

// -----------------------------------------------------------------------------
// PROGRESSO
// -----------------------------------------------------------------------------

export type OptimizationPhase = 'validate' | 'prepare' | 'search' | 'finalize';

export interface ProgressEvent {
  phase: OptimizationPhase;
  /** 0 a 100, monotonico nao-decrescente. */
  percent: number;
  elapsedMs: number;
  /** Combinacoes/tentativas avaliadas ate agora. */
  iterations: number;
  /** Melhor solucao parcial conhecida, ou null antes da primeira. */
  best: {
    numSheets: number;
    usedAreaRatio: number;
    numUnfitParts: number;
  } | null;
}

// -----------------------------------------------------------------------------
// PROJETO
// -----------------------------------------------------------------------------

/** Versao do formato de persistencia. Incrementar ao mudar o schema. */
export const PROJECT_VERSION = 1;

export interface Project {
  /** Versao do schema. Ver `PROJECT_VERSION`. */
  version: number;
  /** Nome exibido no cabecalho e usado no nome dos arquivos exportados. */
  name: string;
  parts: Part[];
  stock: StockSheet[];
  settings: Settings;
  edgeBands: EdgeBandType[];
}
